import { useState, useEffect } from 'react'
import { Save, RefreshCw } from 'lucide-react'
import { useAuth } from '../../context/AuthContext'
import './VodSettings.css'

export default function VodSettings() {
  const { api } = useAuth()

  const [previewMode, setPreviewMode] = useState('random_pct')
  const [previewMinPct, setPreviewMinPct] = useState(23)
  const [previewMaxPct, setPreviewMaxPct] = useState(47)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [status, setStatus] = useState('')

  useEffect(() => {
    loadSettings()
  }, [])

  async function loadSettings() {
    setLoading(true)
    try {
      const data = await api('/settings?prefix=vod_')
      const s = data.settings || {}
      if (s.vod_preview_mode) setPreviewMode(s.vod_preview_mode)
      if (s.vod_preview_min_pct) setPreviewMinPct(parseInt(s.vod_preview_min_pct, 10))
      if (s.vod_preview_max_pct) setPreviewMaxPct(parseInt(s.vod_preview_max_pct, 10))
    } catch (e) {
      setStatus(`Error loading settings: ${e.message}`)
    }
    setLoading(false)
  }

  async function handleSave() {
    if (previewMinPct >= previewMaxPct) {
      setStatus('Error: Min % must be less than Max %')
      return
    }
    if (previewMinPct < 0 || previewMaxPct > 95) {
      setStatus('Error: Values must be between 0% and 95%')
      return
    }

    setSaving(true)
    try {
      await api('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          settings: {
            vod_preview_mode: previewMode,
            vod_preview_min_pct: String(previewMinPct),
            vod_preview_max_pct: String(previewMaxPct),
          }
        })
      })
      setStatus('Settings saved. Changes will apply to all apps within 5 minutes (cache TTL).')
    } catch (e) {
      setStatus(`Error: ${e.message}`)
    }
    setSaving(false)
  }

  if (loading) {
    return (
      <div className="vod-settings">
        <h1>VOD Settings</h1>
        <p style={{ color: 'var(--color-text-muted)' }}>Loading...</p>
      </div>
    )
  }

  return (
    <div className="vod-settings">
      <div className="vod-settings-header">
        <h1>VOD Settings</h1>
        <p className="vod-settings-subtitle">
          Global settings for on-demand video playback across all platforms (Web, Roku, Fire TV)
        </p>
      </div>

      {status && (
        <div className={`vod-settings-status ${status.startsWith('Error') ? 'error' : 'success'}`}>
          {status}
        </div>
      )}

      <div className="vod-settings-card">
        <h3>Preview Position</h3>
        <p className="vod-settings-hint">
          When a viewer selects a video, it previews at a random point within this range before they click "Watch from Start".
          This applies to all platforms.
        </p>

        <div className="vod-settings-field">
          <label>Preview Mode</label>
          <select value={previewMode} onChange={e => setPreviewMode(e.target.value)}>
            <option value="random_pct">Random % of video length</option>
            <option value="fixed_time">Fixed time (legacy)</option>
          </select>
        </div>

        {previewMode === 'random_pct' && (
          <div className="vod-settings-range">
            <div className="vod-settings-field">
              <label>Min Position (%)</label>
              <div className="vod-settings-input-row">
                <input
                  type="range"
                  min={0}
                  max={90}
                  value={previewMinPct}
                  onChange={e => setPreviewMinPct(Number(e.target.value))}
                />
                <input
                  type="number"
                  min={0}
                  max={90}
                  value={previewMinPct}
                  onChange={e => setPreviewMinPct(Number(e.target.value))}
                  className="vod-settings-num"
                />
                <span>%</span>
              </div>
            </div>

            <div className="vod-settings-field">
              <label>Max Position (%)</label>
              <div className="vod-settings-input-row">
                <input
                  type="range"
                  min={5}
                  max={95}
                  value={previewMaxPct}
                  onChange={e => setPreviewMaxPct(Number(e.target.value))}
                />
                <input
                  type="number"
                  min={5}
                  max={95}
                  value={previewMaxPct}
                  onChange={e => setPreviewMaxPct(Number(e.target.value))}
                  className="vod-settings-num"
                />
                <span>%</span>
              </div>
            </div>

            <div className="vod-settings-preview-bar">
              <div className="vod-settings-preview-track">
                <div
                  className="vod-settings-preview-range"
                  style={{ left: `${previewMinPct}%`, width: `${previewMaxPct - previewMinPct}%` }}
                />
              </div>
              <div className="vod-settings-preview-labels">
                <span>0%</span>
                <span className="vod-settings-preview-zone">{previewMinPct}% — {previewMaxPct}%</span>
                <span>100%</span>
              </div>
            </div>

            <p className="vod-settings-example">
              Example: On a 2-hour video, preview will start at a random point between {Math.round(120 * previewMinPct / 100)} min and {Math.round(120 * previewMaxPct / 100)} min.
            </p>
          </div>
        )}

        {previewMode === 'fixed_time' && (
          <p className="vod-settings-hint">
            Fixed time mode uses the legacy 9-minute preview start point for all videos regardless of duration.
          </p>
        )}
      </div>

      <div className="vod-settings-actions">
        <button className="btn-ghost" onClick={loadSettings} disabled={saving}>
          <RefreshCw size={14} /> Reset
        </button>
        <button className="btn-primary" onClick={handleSave} disabled={saving}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>
    </div>
  )
}
